import { useMemo } from 'react';

import { type ChatInputInternalProps } from './types';

type ActionsMenuItemsVisibility = ChatInputInternalProps['actionsMenuItemsVisibility'];
type ActionsMenuItemsVisibilityKey = keyof NonNullable<ActionsMenuItemsVisibility>;

export const useBoundaryActionsMenuItemsVisibility = (
	actionsMenuItemsVisibility: ActionsMenuItemsVisibility,
	isRenderStabilizationEnabled: boolean,
): ActionsMenuItemsVisibility => {
	const isActionsMenuItemsVisibilityDefined = actionsMenuItemsVisibility !== undefined;
	// Keep this scalar list in lock-step with the prop type so the hook avoids
	// JSON.stringify/sort work in the render path while still failing typecheck
	// when a new visibility key needs a dependency review.
	const { fileUpload, mention, format, link, deepResearch } = {
		fileUpload: actionsMenuItemsVisibility?.fileUpload,
		mention: actionsMenuItemsVisibility?.mention,
		format: actionsMenuItemsVisibility?.format,
		link: actionsMenuItemsVisibility?.link,
		deepResearch: actionsMenuItemsVisibility?.deepResearch,
	} satisfies Record<ActionsMenuItemsVisibilityKey, boolean | undefined>;

	const stableActionsMenuItemsVisibility = useMemo(() => {
		if (!isRenderStabilizationEnabled || actionsMenuItemsVisibility === undefined) {
			return actionsMenuItemsVisibility;
		}

		return { ...actionsMenuItemsVisibility };
		// eslint-disable-next-line react-hooks/exhaustive-deps -- scalar fields above represent the object without serializing it each render.
	}, [
		isRenderStabilizationEnabled,
		isActionsMenuItemsVisibilityDefined,
		fileUpload,
		mention,
		format,
		link,
		deepResearch,
	]);

	return isRenderStabilizationEnabled
		? stableActionsMenuItemsVisibility
		: actionsMenuItemsVisibility;
};
